import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';
import { operatorHeaders, requireOperatorToken } from './pos/operatorGrant';

const API_URL = 'pagos/';

export interface ConfiguracionPago {
    id: number;
    nombre: string;
    tipo: 'efectivo' | 'banco' | 'otro';
    afecta_caja: boolean;
    requiere_cambio: boolean;
    activo: boolean;
    fecha_creacion: string;
}

export interface Pago {
    id: number;
    cita: number | null;
    cita_info?: {
        id: number;
        codigo: string;
        total: number;
        monto_pagado: number;
    };
    sesion: number | null;
    afiliacion?: number | null;
    afiliacion_empresa?: string;
    afiliacion_nit?: string;
    venta_cafeteria: number | null;   // ← NUEVO
    // venta_cafeteria_info? (opcional, si el backend lo anida)
    sesion_pos?: number;
    tipo_pago: number;
    tipo_pago_nombre?: string;
    tipo_pago_configuracion?: ConfiguracionPago;
    monto: number;
    efectivo_recibido: number | null;
    cambio: number | null;
    fecha_pago: string;
    codigo: string;
}

// GET: Obtener todos los pagos
export const fetchPagos = async (): Promise<Pago[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Pago[]>(API_URL);
        return response.data;
    });
};

// POST: Crear un pago (ahora permite venta_cafeteria).
// Con ``sesion_pos`` (cobro desde una caja) el backend exige el grant del
// operador identificado por PIN: sin ``operadorToken`` no se llama al servidor.
// Sin caja es un pago administrativo (solo tipos que no afectan a caja).
export const createPago = async (data: {
    documento_reserva?: 'factura' | 'prefactura';
    cita?: number | null;
    sesion?: number | null;
    afiliacion?: number | null;
    venta_cafeteria?: number | null;
    tipo_pago: number;
    monto: number;
    efectivo_recibido?: number;
    cambio?: number;
    sesion_pos?: number;
}, operadorToken?: string | null) => {
    const config = data.sesion_pos != null ? operatorHeaders(requireOperatorToken(operadorToken)) : undefined;
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.post(API_URL, data, config);
        return response.data;
    });
};

// Los pagos son historial financiero append-only. No se exportan helpers
// PUT/PATCH/DELETE: cualquier corrección debe modelarse como transición
// compensatoria autorizada, no como reescritura del pago original.

// ... resto de funciones sin cambios
export const fetchCitasByClienteIda = async (citaId: number): Promise<Pago[]> => {
    const todasLasCitas = await fetchPagos();
    return todasLasCitas.filter(pago => pago.cita === citaId);
};

export const fetchPagosBySesion = async (sesionId: number): Promise<Pago[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Pago[]>(`${API_URL}?sesion=${sesionId}`);
        return response.data;
    });
};

export const fetchPagosByCita = async (citaId: number, directo: boolean = true): Promise<Pago[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Pago[]>(`${API_URL}?cita=${citaId}&directo=${directo}`);
        return response.data;
    });
};

export const fetchPagosByCitaId = async (citaId: number): Promise<Pago[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Pago[]>(`${API_URL}?cita=${citaId}`);
        return response.data;
    });
};

export const fetchSesionesByCita = async (citaId: number) => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`/sesiones/?cita=${citaId}`);
        return response.data;
    });
};

export const fetchPagosPorSesion = async (sesionId: number): Promise<Pago[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Pago[]>(`${API_URL}?sesion=${sesionId}`);
        return response.data;
    });
};

export const fetchResumenSesion = async (sesionId: number): Promise<any> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(`${API_URL}${sesionId}/resumen_citas/`);
        return response.data;
    });
};

export const fetchPagosPorSesionPOS = async (sesionId: number): Promise<Pago[]> => {
    return handleRequestWithAuth(async () => {
        const response = await axiosInstance.get<Pago[]>(`${API_URL}?sesion_pos=${sesionId}`);
        return response.data;
    });
};

